// Users (doc 03 §2) — one page, two audiences.
//
// An NCI admin sees every account in the system and can act on all of them. A
// reseller sees only their own organisation and can only ever create more of
// themselves. The page does not branch into two components for that: the API
// already scopes what comes back, and the role decides which controls render,
// so there is exactly one place where "who may do what" is expressed in the UI
// and it matches the server rule rather than restating it.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type AppUser, type Me, type Page, type Reseller } from "../api";
import {
  Badge,
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  Modal,
  PageHeader,
  Spinner,
  StatusDot,
  formatTime,
  useAction,
  useToast,
} from "../ui";

const ROLE_LABEL: Record<string, string> = {
  nci_admin: "NCI Admin",
  nci_ops: "NCI Ops",
  reseller: "Reseller",
};

const ROLE_HINT: Record<string, string> = {
  nci_admin: "Full control of the fleet, billing and every account.",
  nci_ops: "Can see everything and run operations, but cannot change accounts or billing.",
  reseller: "Sees only their own organisation's Outposts, services and invoices.",
};

export default function Users({ me }: { me: Me }) {
  const isNci = me.role === "nci_admin" || me.role === "nci_ops";
  const canManage = me.role === "nci_admin" || me.role === "reseller";
  const queryClient = useQueryClient();
  const toast = useToast();

  const [resellerFilter, setResellerFilter] = useState<string>("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  // Shown once, then gone — there is no way to read it back out of the system.
  const [issued, setIssued] = useState<{ user: AppUser; password: string } | null>(null);

  const usersQuery = useQuery<Page<AppUser>>({
    queryKey: ["users", resellerFilter],
    queryFn: () => api.get(`/v1/users${resellerFilter ? `?reseller_id=${resellerFilter}` : ""}`),
  });

  const resellersQuery = useQuery<Page<Reseller>>({
    queryKey: ["resellers"],
    queryFn: () => api.get("/v1/resellers?limit=200"),
    enabled: isNci,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["users"] });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) =>
      api.patch<AppUser>(`/v1/users/${id}`, body),
    onSuccess: () => {
      refresh();
      toast("Account updated.", "ok");
    },
    onError: (error) => toast(error instanceof Error ? error.message : String(error), "crit"),
  });

  const resetPassword = useMutation({
    mutationFn: (id: number) => api.post<AppUser>(`/v1/users/${id}/reset_password`, {}),
    onSuccess: (user) => {
      refresh();
      if (user.initial_password) setIssued({ user, password: user.initial_password });
    },
    onError: (error) => toast(error instanceof Error ? error.message : String(error), "crit"),
  });

  if (usersQuery.isLoading) return <Spinner />;
  if (usersQuery.error) return <ErrorNote error={usersQuery.error} />;

  const users = usersQuery.data?.items ?? [];
  const resellers = resellersQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        eyebrow={isNci ? "Access control" : "Your organisation"}
        title={isNci ? "Users" : "Team"}
        subtitle={
          isNci
            ? "Every account that can sign in to Outpost, across NCI and all resellers."
            : `Everyone at ${me.reseller_name ?? "your organisation"} who can sign in to Outpost. Anyone you add sees the same Outposts, services and invoices you do.`
        }
        actions={
          canManage && (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              Add user
            </button>
          )
        }
      />

      {isNci && resellers.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <span className="eyebrow-muted">Filter</span>
          <select
            className="input max-w-xs"
            value={resellerFilter}
            onChange={(e) => setResellerFilter(e.target.value)}
          >
            <option value="">Everyone</option>
            {resellers.map((reseller) => (
              <option key={reseller.id} value={reseller.id}>
                {reseller.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <Card padded={false}>
        {users.length === 0 ? (
          <Empty>No accounts yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  {isNci && <th>Organisation</th>}
                  <th>Status</th>
                  <th>Two-factor</th>
                  <th>Last signed in</th>
                  {canManage && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="font-medium text-ink-100">{user.full_name || user.email}</div>
                      {user.full_name && <div className="text-xs text-ink-400">{user.email}</div>}
                      {user.id === me.id && <div className="text-xs text-brand-on">that&apos;s you</div>}
                    </td>
                    <td>
                      <Badge tone={user.role === "reseller" ? "idle" : "brand"}>{ROLE_LABEL[user.role]}</Badge>
                    </td>
                    {isNci && <td className="text-ink-400">{user.reseller_name ?? "NetComTV"}</td>}
                    <td>
                      <StatusDot
                        tone={user.is_active ? "ok" : "idle"}
                        label={user.is_active ? "active" : "disabled"}
                      />
                    </td>
                    <td>
                      {user.totp_enabled ? (
                        <StatusDot tone="ok" label="enrolled" />
                      ) : user.totp_required_by_policy ? (
                        <StatusDot tone="warn" label="required — enrols at next sign-in" />
                      ) : (
                        <span className="text-ink-400">not required</span>
                      )}
                    </td>
                    <td className="text-ink-400">
                      {user.last_login_at ? formatTime(user.last_login_at) : "never"}
                    </td>
                    {canManage && (
                      <td>
                        <div className="flex justify-end gap-2">
                          <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setEditing(user)}>
                            Edit
                          </button>
                          <ConfirmButton
                            label="Reset password"
                            confirmLabel="Reset — click again"
                            className="px-2 py-1 text-xs"
                            onConfirm={() => resetPassword.mutate(user.id)}
                          />
                          {user.id !== me.id && (
                            <ConfirmButton
                              label={user.is_active ? "Disable" : "Enable"}
                              danger={user.is_active}
                              className="px-2 py-1 text-xs"
                              onConfirm={() => patch.mutate({ id: user.id, body: { is_active: !user.is_active } })}
                            />
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <SelfService me={me} />

      {creating && (
        <CreateUser
          me={me}
          resellers={resellers}
          onClose={() => setCreating(false)}
          onCreated={(user) => {
            setCreating(false);
            refresh();
            if (user.initial_password) setIssued({ user, password: user.initial_password });
          }}
        />
      )}

      {editing && (
        <EditUser
          me={me}
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      {issued && (
        <Modal title="One-time password" onClose={() => setIssued(null)}>
          <p className="mb-3 text-sm text-ink-300">
            This is the only time this password is shown. Outpost stores it hashed and cannot show it
            again — if it is lost, issue another one.
          </p>
          <div className="mb-3 rounded-md border border-ink-600 bg-ink-800 p-3">
            <div className="eyebrow-muted mb-1">{issued.user.email}</div>
            <code className="select-all break-all font-mono text-lg text-brand-on">{issued.password}</code>
          </div>
          <div className="callout">
            Send it over a channel the person already trusts, and have them change it at first sign-in.
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              className="btn-secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(issued.password);
                toast("Copied.", "ok");
              }}
            >
              Copy
            </button>
            <button className="btn-primary" onClick={() => setIssued(null)}>
              Done
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// --- create ----------------------------------------------------------------
function CreateUser({
  me,
  resellers,
  onClose,
  onCreated,
}: {
  me: Me;
  resellers: Reseller[];
  onClose: () => void;
  onCreated: (user: AppUser) => void;
}) {
  const isNci = me.role === "nci_admin" || me.role === "nci_ops";
  const { run, busy } = useAction();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  // A reseller can only ever make another reseller user, so the control is not
  // offered to them at all rather than offered and then refused.
  const [role, setRole] = useState<string>(isNci ? "reseller" : "reseller");
  const [resellerId, setResellerId] = useState<string>(
    isNci ? String(resellers[0]?.id ?? "") : String(me.reseller_id ?? ""),
  );

  const needsReseller = role === "reseller";

  return (
    <Modal
      title="Add user"
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || !email.trim() || (needsReseller && !resellerId)}
            onClick={() =>
              void run(async () => {
                const created = await api.post<AppUser>("/v1/users", {
                  email: email.trim(),
                  full_name: fullName.trim() || null,
                  role,
                  reseller_id: needsReseller ? Number(resellerId) : null,
                });
                onCreated(created);
              })
            }
          >
            {busy ? "Creating…" : "Create user"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Email" hint="This is the sign-in name.">
          <input
            className="input"
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
          />
        </Field>
        <Field label="Full name" hint="Optional, but it makes the audit log readable.">
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>

        {me.role === "nci_admin" ? (
          <Field label="Role" hint={ROLE_HINT[role]}>
            <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="reseller">Reseller</option>
              <option value="nci_ops">NCI Ops</option>
              <option value="nci_admin">NCI Admin</option>
            </select>
          </Field>
        ) : (
          <div className="callout">
            New accounts join <strong>{me.reseller_name}</strong> with the same access you have.
          </div>
        )}

        {me.role === "nci_admin" && needsReseller && (
          <Field label="Organisation">
            <select className="input" value={resellerId} onChange={(e) => setResellerId(e.target.value)}>
              {resellers.map((reseller) => (
                <option key={reseller.id} value={reseller.id}>
                  {reseller.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="callout">
          Outpost generates a strong password and shows it to you once. Nobody has to invent one, and
          there is nothing to intercept in an email.
        </div>
      </div>
    </Modal>
  );
}

// --- edit ------------------------------------------------------------------
function EditUser({
  me,
  user,
  onClose,
  onSaved,
}: {
  me: Me;
  user: AppUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { run, busy } = useAction();
  const [fullName, setFullName] = useState(user.full_name ?? "");
  const [role, setRole] = useState(user.role);

  return (
    <Modal
      title={user.email}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.patch(`/v1/users/${user.id}`, {
                  full_name: fullName.trim() || null,
                  ...(me.role === "nci_admin" && role !== user.role ? { role } : {}),
                });
                onSaved();
              }, "Account updated.")
            }
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Full name">
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>

        {me.role === "nci_admin" && (
          <Field label="Role" hint={ROLE_HINT[role]}>
            <select
              className="input"
              value={role}
              onChange={(e) => setRole(e.target.value as AppUser["role"])}
              disabled={user.id === me.id}
            >
              <option value="reseller">Reseller</option>
              <option value="nci_ops">NCI Ops</option>
              <option value="nci_admin">NCI Admin</option>
            </select>
          </Field>
        )}

        <div className="border-t border-ink-600 pt-4">
          <div className="eyebrow mb-2">Two-factor</div>
          <p className="mb-3 text-sm text-ink-300">
            {user.totp_enabled
              ? "Enrolled. Clear it if they have lost the device — they will be walked through enrolment again at their next sign-in."
              : user.totp_required_by_policy
                ? "Required by policy but not yet enrolled. They will be walked through it at their next sign-in."
                : "Not required for this organisation."}
          </p>
          {user.totp_enabled && (
            <ConfirmButton
              label="Clear two-factor"
              confirmLabel="Clear it — click again"
              danger
              onConfirm={() =>
                void run(async () => {
                  await api.patch(`/v1/users/${user.id}`, { reset_totp: true });
                  onSaved();
                }, "Two-factor cleared. They will re-enrol at next sign-in.")
              }
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

// --- your own account ------------------------------------------------------
function SelfService({ me }: { me: Me }) {
  const { run, busy } = useAction();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const { data: sessions } = useQuery<{ active_sessions: number }>({
    queryKey: ["my-sessions"],
    queryFn: () => api.get("/v1/users/me/sessions"),
  });

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = confirm.length > 0 && next !== confirm;

  return (
    <div className="mt-8">
      <div className="eyebrow mb-2">Your account</div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Change your password">
          <div className="space-y-3">
            <Field label="Current password">
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </Field>
            <Field
              label="New password"
              error={tooShort ? "At least 12 characters." : null}
              hint="At least 12 characters. A passphrase is easier to remember and harder to guess."
            >
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
            </Field>
            <Field label="Confirm new password" error={mismatch ? "These do not match." : null}>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>
            <p className="text-xs text-ink-400">
              Changing your password signs you out everywhere, including here.
            </p>
            <button
              className="btn-primary"
              disabled={busy || !current || next.length < 12 || next !== confirm}
              onClick={() =>
                void run(async () => {
                  await api.post("/v1/auth/password", {
                    current_password: current,
                    new_password: next,
                  });
                  toast("Password changed. Signing you back in…", "ok");
                  setTimeout(() => window.location.reload(), 1200);
                })
              }
            >
              {busy ? "Saving…" : "Change password"}
            </button>
          </div>
        </Card>

        <Card title="Sessions and two-factor">
          <div className="space-y-4">
            <div>
              <div className="eyebrow-muted">Signed in</div>
              <div className="figure mt-1 text-stat text-ink-100">{sessions?.active_sessions ?? "—"}</div>
              <p className="mt-1 text-xs text-ink-400">
                Places this account currently has a live session, including this browser.
              </p>
            </div>
            <ConfirmButton
              label="Sign out everywhere"
              confirmLabel="Sign out everywhere — click again"
              danger
              onConfirm={() =>
                void run(async () => {
                  await api.post("/v1/users/me/sessions/revoke", {});
                  setTimeout(() => window.location.reload(), 800);
                })
              }
            />

            <div className="border-t border-ink-600 pt-4">
              <div className="eyebrow-muted mb-1">Two-factor</div>
              <p className="mb-3 text-sm text-ink-300">
                {me.totp_enabled
                  ? "Enrolled on this account."
                  : "Not enrolled. If your organisation turns it on you will be asked to set it up at your next sign-in."}
              </p>
              {me.totp_enabled && (
                <ConfirmButton
                  label="Move to a new device"
                  confirmLabel="Reset it — click again"
                  onConfirm={() =>
                    void run(async () => {
                      await api.post("/v1/users/me/totp/reset", {});
                      setTimeout(() => window.location.reload(), 800);
                    })
                  }
                />
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
